import { useState, useEffect } from 'react';
import { format, addDays, startOfWeek, isSameDay } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useAuthStore } from '@shared/store/authStore';

interface GroupLesson {
  lesson_id: string;
  service: {
    service_id: number;
    service_name: string;
  };
  place_id: number;
  trainer_id: number;
  trainer_name?: string;
  date: string;
  start_time: string;
  end_time: string;
  commercial: boolean;
  color: string;
  clients: number[];
  max_clients?: number;
}

interface Employee {
  Id: number;
  first_name: string;
  last_name: string;
  middle_name: string;
  spec_name: string;
}

export function GroupLessonsPage() {
  const { user, apiClient } = useAuthStore();
  const [lessons, setLessons] = useState<GroupLesson[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [selectedType, setSelectedType] = useState<string>('all');
  const [registering, setRegistering] = useState<string | null>(null);
  const [selectedMobileDate, setSelectedMobileDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // Service types for filtering
  const serviceTypes = [
    { key: 'all', name: 'Все занятия', icon: '🏃‍♀️' },
    { key: 'ЙОГА', name: 'Йога', icon: '🧘‍♀️' },
    { key: 'TRX', name: 'TRX', icon: '💪' },
    { key: 'TABATA', name: 'Tabata', icon: '🔥' },
    { key: 'CROSSFIT', name: 'Crossfit', icon: '🏋️‍♀️' },
    { key: 'STRETCHING', name: 'Stretching', icon: '🤸‍♀️' },
    { key: 'ТАНЦЫ', name: 'Танцы', icon: '💃' },
    { key: 'STEP', name: 'Step', icon: '👟' },
    { key: 'FUNCIONAL', name: 'Functional', icon: '⚡' },
  ];

  // Generate week dates starting from today
  const today = new Date();
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(today, i);
    return {
      date,
      dateStr: format(date, 'yyyy-MM-dd'),
      dayName: format(date, 'EEEE', { locale: ru }),
      dayNameShort: format(date, 'EEEEEE', { locale: ru }), // короткая форма (Пн, Вт, Ср...)
      dayNumber: format(date, 'd'),
      isToday: i === 0
    };
  });

  useEffect(() => {
    loadGroupLessons();
    loadEmployees();
  }, [selectedWeek, apiClient]);

  const loadGroupLessons = async () => {
    if (!apiClient) return;

    setIsLoading(true);
    try {
      // Используем сегодня как начало периода
      const today = new Date();
      const weekStart = format(today, 'yyyy-MM-dd');
      const weekEnd = format(addDays(today, 6), 'yyyy-MM-dd');

      const response = await apiClient.getGroupLessons(weekStart, weekEnd);
      setLessons(response);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const loadEmployees = async () => {
    if (!apiClient) return;

    try {
      const response = await apiClient.getEmployees();
      setEmployees(response);
    } catch (error) {
    }
  };

  const getTrainerName = (trainerId: number): string => {
    const trainer = employees.find(emp => emp.Id === trainerId);
    return trainer ? `${trainer.first_name} ${trainer.last_name}` : `Тренер ${trainerId}`;
  };

  const getServiceIcon = (serviceName: string): string => {
    const service = serviceTypes.find(type => serviceName.includes(type.key));
    return service?.icon || '🏃‍♀️';
  };

  const filteredLessons = lessons.filter(lesson => {
    if (selectedType === 'all') return true;
    return lesson.service.service_name.includes(selectedType);
  });

  const getLessonsByDay = (dateStr: string) => {
    return filteredLessons
      .filter(lesson => lesson.date === dateStr)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  const handleRegister = async (lessonId: string) => {
    if (!user || !apiClient) return;

    setRegistering(lessonId);
    try {
      await apiClient.registerClientForTraining(user.id, lessonId);
      alert('Вы успешно записались на занятие!');
      // Reload lessons to update client count
      await loadGroupLessons();
    } catch (error: any) {
      const errorMessage = error.message || 'Ошибка при записи на занятие';
      alert(errorMessage);
    } finally {
      setRegistering(null);
    }
  };

  const getAvailableSpots = (lesson: GroupLesson): number => {
    const maxClients = lesson.max_clients || 15; // Default max clients
    return Math.max(0, maxClients - lesson.clients.length);
  };

  const isClientRegistered = (lesson: GroupLesson): boolean => {
    return lesson.clients.includes(user?.id || 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Групповые занятия</h1>
        {isLoading && (
          <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
        )}
      </div>


      {/* Week Navigation */}
      <div className="hidden md:flex items-center justify-center rounded-2xl p-4 shadow-sm" style={{ backgroundColor: 'var(--card-bg)' }}>
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          {format(weekDates[0].date, 'd MMMM', { locale: ru })} - {format(weekDates[6].date, 'd MMMM yyyy', { locale: ru })}
        </h2>
      </div>

      {/* Week Days */}
      <div className="hidden md:grid md:grid-cols-7 gap-4 mb-6">
        {weekDates.map((day) => {
          const dayLessons = getLessonsByDay(day.dateStr);
          
          return (
            <div key={day.dateStr} className="rounded-2xl p-4 shadow-sm" style={{ backgroundColor: 'var(--card-bg)' }}>
              <div className="text-center mb-4">
                <h3 className="font-semibold capitalize text-sm" style={{ color: 'var(--text-primary)' }}>{day.dayName}</h3>
                <p className="text-xl font-bold text-emerald-600">{day.dayNumber}</p>
                {day.isToday && (
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-800 text-xs rounded-full">
                    Сегодня
                  </span>
                )}
              </div>

              <div className="space-y-2">
                {dayLessons.length > 0 ? (
                  dayLessons.map((lesson) => {
                    const availableSpots = getAvailableSpots(lesson);
                    const isRegistered = isClientRegistered(lesson);
                    const isPastTime = new Date(`${lesson.date}T${lesson.end_time}`) < new Date();
                    
                    return (
                      <div key={lesson.lesson_id} className="border rounded-lg p-2" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--card-bg)' }}>
                        <div className="text-center mb-2">
                          <span className="text-sm">{getServiceIcon(lesson.service.service_name)}</span>
                          <h4 className="font-medium text-xs" style={{ color: 'var(--text-primary)' }}>
                            {lesson.service.service_name}
                          </h4>
                          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            {lesson.start_time.slice(0, 5)}
                          </p>
                        </div>

                        <div className="text-center">
                          {isRegistered ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                              Записан
                            </span>
                          ) : isPastTime ? (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                              Прошло
                            </span>
                          ) : availableSpots > 0 ? (
                            <button
                              onClick={() => handleRegister(lesson.lesson_id)}
                              disabled={registering === lesson.lesson_id}
                              className="px-2 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700 disabled:opacity-50"
                            >
                              {registering === lesson.lesson_id ? '...' : 'Записаться'}
                            </button>
                          ) : (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                              Занято
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-4">
                    <div className="text-2xl mb-1">📅</div>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Нет занятий</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile View */}
      <div className="md:hidden">
        {/* Mobile Date Selection */}
        <div className="rounded-2xl p-4 shadow-sm mb-4" style={{ backgroundColor: 'var(--card-bg)' }}>
          <div className="flex space-x-2 overflow-x-auto pb-2">
            {weekDates.map((day) => (
              <button
                key={day.dateStr}
                onClick={() => setSelectedMobileDate(day.dateStr)}
                className={`flex-shrink-0 p-3 border rounded-lg text-center transition-colors min-w-[70px] ${
                  selectedMobileDate === day.dateStr
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'hover:border-emerald-500 hover:bg-emerald-50'
                }`}
                style={{
                  borderColor: selectedMobileDate === day.dateStr ? undefined : 'var(--border-color)',
                  backgroundColor: selectedMobileDate === day.dateStr ? undefined : 'var(--card-bg)',
                  color: selectedMobileDate === day.dateStr ? undefined : 'var(--text-primary)'
                }}
              >
                <div className="text-xs font-medium capitalize">{day.dayNameShort}</div>
                <div className="text-lg font-bold">{day.dayNumber}</div>
                {day.isToday && <div className="text-xs text-emerald-600">Сегодня</div>}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile Lessons for Selected Date */}
        <div className="rounded-2xl p-4 shadow-sm" style={{ backgroundColor: 'var(--card-bg)' }}>
          <div className="space-y-3">
            {(() => {
              const selectedDayLessons = getLessonsByDay(selectedMobileDate);
              return selectedDayLessons.length > 0 ? (
                selectedDayLessons.map((lesson) => {
                  const availableSpots = getAvailableSpots(lesson);
                  const isRegistered = isClientRegistered(lesson);
                  const isPastTime = new Date(`${lesson.date}T${lesson.end_time}`) < new Date();
                  
                  return (
                    <div key={lesson.lesson_id} className="border rounded-lg p-3" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--card-bg)' }}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{getServiceIcon(lesson.service.service_name)}</span>
                          <div>
                            <h4 className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                              {lesson.service.service_name}
                            </h4>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                              {getTrainerName(lesson.trainer_id)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            {lesson.start_time.slice(0, 5)}
                          </div>
                          <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            {lesson.end_time.slice(0, 5)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            {lesson.clients.length} чел.
                          </span>
                          {availableSpots > 0 && (
                            <span className="text-xs text-green-600">
                              +{availableSpots} мест
                            </span>
                          )}
                          {availableSpots === 0 && (
                            <span className="text-xs text-red-600">
                              Мест нет
                            </span>
                          )}
                        </div>

                        {isRegistered ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                            Записан
                          </span>
                        ) : isPastTime ? (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                            Прошло
                          </span>
                        ) : availableSpots > 0 ? (
                          <button
                            onClick={() => handleRegister(lesson.lesson_id)}
                            disabled={registering === lesson.lesson_id}
                            className="px-2 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700 disabled:opacity-50"
                          >
                            {registering === lesson.lesson_id ? '...' : 'Записаться'}
                          </button>
                        ) : (
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                            Занято
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">📅</div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Занятий нет</p>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

    </div>
  );
}